// src/hooks/useFundData.js - With corrected ETH price detection
import { useState, useEffect, useCallback, useRef } from "react";
import { ethers } from "ethers";
import { 
  getEthBalance,
  fetchNav,
  fetchFundEthBalance,
  fetchFundTokenBalance,
  fetchWalletTokenBalance
} from "../services/web3Service";
import { fetchTokens, enhanceTokens } from "../services/tokenService";
import { CONTRACT_ADDRESS } from "../constants/contractInfo";
import { useTokenPrices } from "./useTokenPrices";
import tokenPriceStore from "../services/tokenPriceStore"; // Import tokenPriceStore directly

// Helper functions for position price integration
const enrichPositionsWithPrices = (positions, prices) => {
  if (!positions || !prices || Object.keys(prices).length === 0) {
    console.warn("Cannot enrich positions - missing data", { 
      positionsLength: positions?.length, 
      pricesLength: Object.keys(prices || {}).length 
    });
    return positions || [];
  }

  return positions.map(position => {
    const normalizedAddress = position.address.toLowerCase();
    const price = prices[normalizedAddress] || 0;
    const usdValue = parseFloat(position.balance) * price;
    
    return {
      ...position,
      priceUsd: price,
      valueUsd: usdValue || 0
    };
  });
};

// FIXED: Reliable ETH address finder that uses consistent identification methods
const findEthAddress = (positions, tokenPrices) => {
  // List of known Ethereum addresses in priority order
  const KNOWN_ETH_ADDRESSES = [
    "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2".toLowerCase(), // WETH on Ethereum (primary reference)
    "0x7ceB23fD6bC0adD59E62ac25578270cFf1b9f619".toLowerCase(), // Polygon ETH 
    "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee".toLowerCase(), // Generic ETH address
    "0x0000000000000000000000000000000000000000".toLowerCase(), // Sometimes used for native ETH
    "0x4200000000000000000000000000000000000006".toLowerCase()  // WETH on Optimism
  ];
  
  // 1. First try to get ETH price directly from tokenPriceStore for the most reliable address
  for (const ethAddr of KNOWN_ETH_ADDRESSES) {
    const price = tokenPriceStore.getPrice(ethAddr);
    if (price && price > 0) {
      console.log(`Found ETH price from tokenPriceStore for ${ethAddr}: ${price}`);
      return ethAddr;
    }
  }

  // 2. Check token prices for all known ETH addresses in order of priority
  for (const ethAddr of KNOWN_ETH_ADDRESSES) {
    if (tokenPrices[ethAddr] && tokenPrices[ethAddr] > 0) {
      console.log(`Found ETH price in tokenPrices for address ${ethAddr}: $${tokenPrices[ethAddr]}`);
      return ethAddr;
    }
  }
  
  // 3. Try to find in positions by symbol or name
  if (positions && positions.length > 0) {
    const ethPosition = positions.find(p => 
      (p.symbol && (p.symbol.toLowerCase() === 'eth' || p.symbol.toLowerCase() === 'weth')) || 
      (p.name && (p.name.toLowerCase().includes('ethereum') || p.name.toLowerCase().includes('ether')))
    );
    
    if (ethPosition) {
      console.log(`Found ETH by name/symbol: ${ethPosition.symbol} at ${ethPosition.address}`);
      return ethPosition.address.toLowerCase();
    }
  }
  
  // 4. Look for tokens in the price range typically associated with ETH (between $1500-5000)
  // This avoids mistaking BTC (which is much more expensive) for ETH
  if (tokenPrices && Object.keys(tokenPrices).length > 0) {
    const ethPriceRange = Object.entries(tokenPrices).filter(([, price]) => 
      price >= 1500 && price <= 5000
    );
    
    if (ethPriceRange.length > 0) {
      console.log(`Found potential ETH by price range: ${ethPriceRange[0][0]} - $${ethPriceRange[0][1]}`);
      return ethPriceRange[0][0].toLowerCase();
    }
  }
  
  // 5. If all else fails, return the primary ETH address
  console.warn(`Could not find ETH address, returning primary reference: ${KNOWN_ETH_ADDRESSES[0]}`);
  return KNOWN_ETH_ADDRESSES[0];
};

const debugPositions = (positions) => {
  if (!positions || positions.length === 0) {
    console.log("No positions to debug");
    return;
  }
  
  console.log("Position details:");
  positions.forEach((pos, index) => {
    console.log(`Position ${index + 1}:`, {
      symbol: pos.symbol,
      address: pos.address,
      balance: pos.balance,
      price: pos.priceUsd || "N/A",
      value: pos.valueUsd || "N/A"
    });
  });
};

export const useFundData = (web3) => {
  const { provider, signer, contract, account } = web3 || {};
  
  const [nav, setNav] = useState("0");
  const [calculatedNav, setCalculatedNav] = useState("0");
  const [walletEthBalance, setWalletEthBalance] = useState("0");
  const [fundEthBalance, setFundEthBalance] = useState("0");
  const [fundTokenBalance, setFundTokenBalance] = useState("0");
  const [walletTokenBalance, setWalletTokenBalance] = useState("0");
  const [tokenPrice, setTokenPrice] = useState("0");
  const [totalSupply, setTotalSupply] = useState("0");
  const [rawPositions, setRawPositions] = useState([]); // Store positions without prices
  const [positions, setPositions] = useState([]);      // Store positions with prices
  const [tokenAddresses, setTokenAddresses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [ethAddress, setEthAddress] = useState(null); // Store the detected ETH address to improve consistency
  
  // Use refs to keep track of the current state without causing re-renders
  const rawPositionsRef = useRef(rawPositions);
  useEffect(() => {
    rawPositionsRef.current = rawPositions;
  }, [rawPositions]);

  // Fetch token addresses to pass to token price hook
  const fetchTokenAddresses = useCallback(async () => {
    if (!contract) return [];
    
    try {
      const tokens = await fetchTokens(contract);
      console.log("Found", tokens.length, "tokens in the fund");
      const addresses = tokens.map(token => token.address.toLowerCase());
      
      // Include all known ETH addresses for pricing
      const ETH_ADDRESSES = [
        "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2".toLowerCase(), // WETH on Ethereum
        "0x7ceB23fD6bC0adD59E62ac25578270cFf1b9f619".toLowerCase(), // Polygon ETH
        "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee".toLowerCase(), // Generic ETH address
        "0x0000000000000000000000000000000000000000".toLowerCase(), // Sometimes used for native ETH
        "0x4200000000000000000000000000000000000006".toLowerCase(), // WETH on Optimism
      ];
      
      // Add all ETH addresses that aren't already in the list
      ETH_ADDRESSES.forEach(addr => {
        if (!addresses.includes(addr)) {
          addresses.push(addr);
        }
      });
      
      console.log("Token addresses for price tracking:", addresses);
      setTokenAddresses(addresses);
      return addresses;
    } catch (error) {
      console.error("Error fetching token addresses:", error);
      return [];
    }
  }, [contract]);

  // Effect to fetch token addresses once on mount
  useEffect(() => {
    if (contract && account) {
      fetchTokenAddresses();
    }
  }, [contract, account, fetchTokenAddresses]);

  // Use token prices hook with our token addresses
  const { 
    prices: tokenPrices, 
    isLoading: pricesLoading, 
    error: pricesError,
    lastUpdated: pricesUpdated
  } = useTokenPrices({ 
    provider, 
    tokenAddresses, 
    refreshInterval: 60000 // 60 seconds
  });
  
  // Track previous token prices to avoid unnecessary updates
  const prevTokenPricesRef = useRef({});

  // Function to calculate NAV from token positions and prices
  const calculateNavFromPositions = useCallback((currentPositions, currentPrices, fundEthBalance = 0) => {
    if (!currentPositions || currentPositions.length === 0 || !currentPrices || Object.keys(currentPrices).length === 0) {
      return null;
    }
    
    // Find ETH address or use cached one for consistency
    const ethAddr = ethAddress || findEthAddress(currentPositions, currentPrices);
    
    // If we found an ETH address, cache it for consistency
    if (ethAddr && !ethAddress) {
      setEthAddress(ethAddr);
    }
    
    let ethPrice = currentPrices[ethAddr] || 0;
    
    // If we don't have ETH price, try to get it directly from tokenPriceStore
    if (ethPrice <= 0) {
      // Try each known ETH address in tokenPriceStore
      const KNOWN_ETH_ADDRESSES = [
        "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2".toLowerCase(),
        "0x7ceB23fD6bC0adD59E62ac25578270cFf1b9f619".toLowerCase(),
        "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee".toLowerCase(),
        "0x0000000000000000000000000000000000000000".toLowerCase(),
        "0x4200000000000000000000000000000000000006".toLowerCase()
      ];
      
      for (const addr of KNOWN_ETH_ADDRESSES) {
        const price = tokenPriceStore.getPrice(addr);
        if (price > 0) {
          console.log(`Found ETH price from tokenPriceStore for ${addr}: $${price}`);
          ethPrice = price;
          break;
        }
      }
    }
    
    // If still no ETH price, look for tokens in the typical ETH price range (not BTC price range)
    if (ethPrice <= 0) {
      console.warn(`ETH price not available for address ${ethAddr}, using fallback methods`);
      
      // Look for tokens in the ETH price range (between $1000-5000) 
      // This helps avoid mistaking BTC for ETH
      const ethPriceRange = Object.entries(currentPrices).filter(([, price]) => 
        price >= 1000 && price <= 5000
      );
      
      if (ethPriceRange.length > 0) {
        console.log(`Found potential ETH by price range: ${ethPriceRange[0][0]} - $${ethPriceRange[0][1]}`);
        ethPrice = ethPriceRange[0][1];
      }
    }
    
    // If still no ETH price, use a default value
    if (ethPrice <= 0) {
      console.warn("No ETH price found, using default value of $3200");
      ethPrice = 3200; // Default ETH price as fallback
    }
    
    // Calculate total USD value of all tokens in the portfolio
    let totalValueUsd = 0;
    
    // Add all token positions
    currentPositions.forEach(token => {
      const normalizedAddress = token.address.toLowerCase();
      const tokenPrice = currentPrices[normalizedAddress] || 0;
      const tokenBalance = parseFloat(token.balance) || 0;
      const tokenValueUsd = tokenBalance * tokenPrice;
      
      console.log(`Token: ${token.address}, Balance: ${tokenBalance}, Price: $${tokenPrice}, Value: $${tokenValueUsd.toFixed(2)}`);
      
      totalValueUsd += tokenValueUsd;
    });
    
    // Add ETH balance value
    if (fundEthBalance > 0) {
      const ethValueUsd = fundEthBalance * ethPrice;
      console.log(`ETH Balance: ${fundEthBalance} ETH, Value: $${ethValueUsd.toFixed(2)}`);
      totalValueUsd += ethValueUsd;
    }
    
    console.log(`Total portfolio value: $${totalValueUsd.toFixed(2)}`);
    console.log(`ETH price: $${ethPrice.toFixed(2)}`);
    
    // Calculate NAV in ETH: Total Portfolio Value in USD / ETH price in USD
    const calculatedNavEth = totalValueUsd / ethPrice;
    
    console.log(`Calculated NAV: ${calculatedNavEth.toFixed(4)} ETH`);
    
    return {
      navEth: calculatedNavEth.toString(),
      totalValueUsd,
      ethPrice
    };
  }, [ethAddress]);

  // Fetch all fund data - used for initial load and manual refreshes
  const fetchAllData = useCallback(async () => {
    if (!contract || !account) return;
    
    setIsLoading(true);
    setError("");
    
    try {
      // Fetch balances and data
      const [
        navValue,
        ethBalance,
        fundEth,
        fundToken,
        walletToken
      ] = await Promise.all([
        fetchNav(contract),
        getEthBalance(signer),
        fetchFundEthBalance(contract, account),
        fetchFundTokenBalance(contract, account),
        fetchWalletTokenBalance(contract, account)
      ]);
      
      setNav(navValue);
      setWalletEthBalance(ethBalance);
      setFundEthBalance(fundEth);
      setFundTokenBalance(fundToken);
      setWalletTokenBalance(walletToken);
      
      // Fetch token price directly from contract and total supply
      try {
        // Fetch the total supply of tokens
        const totalSupplyRaw = await contract.totalSupply();
        const totalSupplyFormatted = ethers.utils.formatEther(totalSupplyRaw);
        setTotalSupply(totalSupplyFormatted);
        
        // Use the contract's viewSharePrice function for accurate price
        const contractSharePrice = await contract.viewSharePrice();
        setTokenPrice(ethers.utils.formatEther(contractSharePrice));
      } catch (error) {
        console.error("Error fetching token price or supply:", error);
        setTokenPrice("0");
        setTotalSupply("0");
      }
      
      // Fetch token positions and enhance with price data from our oracle
      const tokens = await fetchTokens(contract);
      
      // If we have new tokens, update the tokenAddresses
      if (tokens && tokens.length > 0) {
        // Include all known ETH addresses for consistency
        const addresses = tokens.map(token => token.address.toLowerCase());
        const ETH_ADDRESSES = [
          "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2".toLowerCase(),
          "0x7ceB23fD6bC0adD59E62ac25578270cFf1b9f619".toLowerCase(),
          "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee".toLowerCase(),
          "0x0000000000000000000000000000000000000000".toLowerCase(),
          "0x4200000000000000000000000000000000000006".toLowerCase()
        ];
        
        ETH_ADDRESSES.forEach(addr => {
          if (!addresses.includes(addr)) {
            addresses.push(addr);
          }
        });
        
        setTokenAddresses(addresses);
      }
      
      // Enhance tokens with balances
      const enhancedTokensData = await enhanceTokens(tokens, provider, CONTRACT_ADDRESS);
      
      // Store the raw positions
      console.log("Raw positions loaded:", enhancedTokensData);
      setRawPositions(enhancedTokensData);
      
      // Now enhance with prices if we have them
      if (tokenPrices && Object.keys(tokenPrices).length > 0) {
        const tokensWithPrices = enrichPositionsWithPrices(enhancedTokensData, tokenPrices);
        console.log("Positions with prices:", tokensWithPrices);
        setPositions(tokensWithPrices);

        // Calculate NAV based on token prices and include ETH balance
        const fundEthBalanceValue = parseFloat(fundEth);
        const navCalc = calculateNavFromPositions(tokensWithPrices, tokenPrices, fundEthBalanceValue);
        if (navCalc) {
          setCalculatedNav(navCalc.navEth);
          console.log(`Calculated NAV: ${navCalc.navEth} ETH (${navCalc.totalValueUsd.toFixed(2)} USD / ${navCalc.ethPrice.toFixed(2)} USD/ETH)`);
        }
      } else {
        // If prices aren't loaded yet, use raw positions
        setPositions(enhancedTokensData);
      }
      
      // Save current prices to ref to avoid unnecessary recalculations
      prevTokenPricesRef.current = { ...tokenPrices };
      
      // Update last updated timestamp
      setLastUpdated(new Date());
      
      // Debug the positions to help diagnose issues
      debugPositions(positions);
      
      return true;
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to load fund data. Please refresh.");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [contract, account, provider, signer, tokenPrices, calculateNavFromPositions]); 

  // Fetch data on initial load
  useEffect(() => {
    if (contract && account) {
      fetchAllData();
    }
  }, [contract, account, fetchAllData]);

  // Update positions and NAV when tokenPrices update
  useEffect(() => {
    // Only update if we have positions and new prices
    if (
      rawPositionsRef.current.length > 0 && 
      tokenPrices && 
      Object.keys(tokenPrices).length > 0
    ) {
      console.log("Prices updated, enhancing positions with new price data");
      
      // Update positions with new prices
      const updatedPositions = enrichPositionsWithPrices(rawPositionsRef.current, tokenPrices);
      setPositions(updatedPositions);
      
      // Get current ETH balance for NAV calculation
      const currentFundEthBalance = parseFloat(fundEthBalance);
      
      // Recalculate NAV based on new token prices and include ETH balance
      const navCalc = calculateNavFromPositions(updatedPositions, tokenPrices, currentFundEthBalance);
      if (navCalc) {
        setCalculatedNav(navCalc.navEth);
        console.log(`Updated NAV: ${navCalc.navEth} ETH (${navCalc.totalValueUsd.toFixed(2)} USD / ${navCalc.ethPrice.toFixed(2)} USD/ETH)`);
      }
      
      // Save current prices to ref
      prevTokenPricesRef.current = { ...tokenPrices };
      
      // Update timestamp
      setLastUpdated(new Date());
      
      // Debug the positions to help diagnose issues
      debugPositions(updatedPositions);
    }
  }, [tokenPrices, calculateNavFromPositions, fundEthBalance]);

  // Combine loading and error states
  const combinedIsLoading = isLoading || pricesLoading;
  const combinedError = error || pricesError;

  // Choose which NAV to display - use calculated NAV if available
  const displayNav = calculatedNav && parseFloat(calculatedNav) > 0 ? calculatedNav : nav;

  return {
    nav: displayNav, // Use calculated NAV when available
    contractNav: nav, // Original NAV from contract
    calculatedNav,    // NAV calculated from token prices
    walletEthBalance,
    fundEthBalance,
    fundTokenBalance,
    walletTokenBalance,
    tokenPrice,
    totalSupply,
    positions,
    tokenPrices,
    isLoading: combinedIsLoading,
    error: combinedError,
    lastUpdated: lastUpdated || pricesUpdated,
    fetchAllData,
    setError
  };
};